import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import type { ComponentProps } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { CheckIcon, ChevronRightIcon, CircleIcon, MinusIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { typographyVariants } from './typography'

const dropdownMenuContentVariants = cva(
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) max-w-[calc(100vw-2rem)] origin-(--radix-dropdown-menu-content-transform-origin) outline-hidden',
  {
    variants: {
      size: {
        content: 'min-w-[8rem]',
        fit: 'min-w-max',
        xs: 'w-40',
        sm: 'w-48',
        md: 'w-56',
        lg: 'w-64',
        xl: 'w-72',
        '2xl': 'w-80',
        panel: 'w-96',
        trigger: 'w-(--radix-dropdown-menu-trigger-width) min-w-56',
      },
      padding: {
        none: 'p-0',
        default: 'p-1',
        compact: 'p-2',
        spacious: 'p-3',
      },
      surface: {
        default: 'rounded-md bg-popover text-popover-foreground shadow-md',
        panel: 'rounded-xl bg-background text-foreground shadow-xl',
      },
      tone: {
        default: 'ring-1 ring-foreground/10',
        destructive:
          'border border-status-destructive-border ring-1 ring-status-destructive-border',
      },
      overflow: {
        auto: 'overflow-x-hidden overflow-y-auto',
        hidden: 'overflow-hidden',
        visible: 'overflow-visible',
      },
    },
    defaultVariants: {
      size: 'content',
      padding: 'default',
      surface: 'default',
      tone: 'default',
      overflow: 'auto',
    },
  }
)

const dropdownMenuItemVariants = cva(
  "[&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:bg-muted data-[disabled]:text-muted-foreground data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'focus:bg-accentSecondary focus:text-accentSecondary-foreground',
        selected:
          'bg-accentSecondary text-accentSecondary-foreground focus:bg-accentSecondary focus:text-accentSecondary-foreground',
        destructive:
          'text-status-destructive-foreground focus:bg-status-destructive-background focus:text-status-destructive-foreground *:[svg]:!text-status-destructive-foreground',
      },
      size: {
        default: 'rounded-sm px-2 py-1.5 text-sm',
        comfortable: 'rounded-md px-3 py-2 text-sm font-medium',
        descriptive:
          'items-start rounded-lg bg-elevated px-3 py-3 text-left text-sm ring-1 ring-border/25 focus:ring-primary/20',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export type DropdownMenuProps = ComponentProps<typeof DropdownMenuPrimitive.Root>
export type DropdownMenuPortalProps = ComponentProps<typeof DropdownMenuPrimitive.Portal>
export type DropdownMenuTriggerProps = ComponentProps<typeof DropdownMenuPrimitive.Trigger>
export type DropdownMenuContentProps = ComponentProps<typeof DropdownMenuPrimitive.Content> &
  VariantProps<typeof dropdownMenuContentVariants> & {
    portalled?: boolean
  }
export type DropdownMenuArrowProps = ComponentProps<typeof DropdownMenuPrimitive.Arrow>
export type DropdownMenuGroupProps = ComponentProps<typeof DropdownMenuPrimitive.Group>
export type DropdownMenuItemProps = ComponentProps<typeof DropdownMenuPrimitive.Item> &
  VariantProps<typeof dropdownMenuItemVariants> & {
    inset?: boolean
  }
export type DropdownMenuCheckboxItemProps = ComponentProps<
  typeof DropdownMenuPrimitive.CheckboxItem
>
export type DropdownMenuRadioGroupProps = ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>
export type DropdownMenuRadioItemProps = ComponentProps<typeof DropdownMenuPrimitive.RadioItem>
export type DropdownMenuLabelVariant = 'default' | 'eyebrow' | 'plain'
export type DropdownMenuLabelProps = ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean
  variant?: DropdownMenuLabelVariant
}
export type DropdownMenuSeparatorProps = ComponentProps<typeof DropdownMenuPrimitive.Separator>
export type DropdownMenuShortcutProps = ComponentProps<'span'>
export type DropdownMenuSubProps = ComponentProps<typeof DropdownMenuPrimitive.Sub>
export type DropdownMenuSubTriggerProps = ComponentProps<
  typeof DropdownMenuPrimitive.SubTrigger
> & {
  inset?: boolean
}
export type DropdownMenuSubContentProps = ComponentProps<typeof DropdownMenuPrimitive.SubContent> &
  VariantProps<typeof dropdownMenuContentVariants>

function DropdownMenu({ ...props }: DropdownMenuProps) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" modal={false} {...props} />
}

function DropdownMenuPortal({ ...props }: DropdownMenuPortalProps) {
  return <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
}

function DropdownMenuTrigger({ ...props }: DropdownMenuTriggerProps) {
  return <DropdownMenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />
}

function DropdownMenuContent({
  className,
  sideOffset = 4,
  size = 'content',
  padding = 'default',
  surface = 'default',
  tone = 'default',
  overflow = 'auto',
  portalled = true,
  ...props
}: DropdownMenuContentProps) {
  const content = (
    <DropdownMenuPrimitive.Content
      data-slot="dropdown-menu-content"
      data-overflow={overflow}
      data-padding={padding}
      data-size={size}
      data-surface={surface}
      data-tone={tone}
      sideOffset={sideOffset}
      className={cn(
        dropdownMenuContentVariants({ size, padding, surface, tone, overflow }),
        className
      )}
      {...props}
    />
  )

  return portalled ? (
    <DropdownMenuPrimitive.Portal>{content}</DropdownMenuPrimitive.Portal>
  ) : (
    content
  )
}

function DropdownMenuArrow({ className, ...props }: DropdownMenuArrowProps) {
  return (
    <DropdownMenuPrimitive.Arrow
      data-slot="dropdown-menu-arrow"
      width={16}
      height={8}
      className={cn('fill-popover', className)}
      {...props}
    />
  )
}

function DropdownMenuGroup({ ...props }: DropdownMenuGroupProps) {
  return <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
}

function DropdownMenuItem({
  className = '',
  inset,
  variant = 'default',
  size = 'default',
  ...props
}: DropdownMenuItemProps) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      data-size={size}
      className={cn(dropdownMenuItemVariants({ variant, size }), className)}
      {...props}
    />
  )
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: DropdownMenuCheckboxItemProps) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "focus:bg-accentSecondary focus:text-accentSecondary-foreground group relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:bg-muted data-[disabled]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      checked={checked}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon className="size-4 group-data-[state=indeterminate]:hidden" />
          <MinusIcon className="hidden size-4 group-data-[state=indeterminate]:block" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
}

function DropdownMenuRadioGroup({ ...props }: DropdownMenuRadioGroupProps) {
  return <DropdownMenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />
}

function DropdownMenuRadioItem({ className, children, ...props }: DropdownMenuRadioItemProps) {
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "focus:bg-accentSecondary focus:text-accentSecondary-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:bg-muted data-[disabled]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <CircleIcon className="size-2 fill-current" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
}

function DropdownMenuLabel({
  className,
  inset,
  variant = 'default',
  ...props
}: DropdownMenuLabelProps) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        variant === 'default' && typographyVariants({ variant: 'label', tone: 'muted' }),
        variant === 'default' && 'px-2 py-1.5 data-[inset]:pl-8',
        variant === 'eyebrow' && typographyVariants({ variant: 'eyebrow' }),
        variant === 'eyebrow' && 'cursor-default px-3 pt-2 pb-1 select-none',
        variant === 'plain' && 'p-0',
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuSeparator({ className, ...props }: DropdownMenuSeparatorProps) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('bg-border -mx-1 my-1 h-px', className)}
      {...props}
    />
  )
}

function DropdownMenuShortcut({ className, ...props }: DropdownMenuShortcutProps) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn('text-muted-foreground ml-auto text-xs tracking-widest', className)}
      {...props}
    />
  )
}

function DropdownMenuSub({ ...props }: DropdownMenuSubProps) {
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: DropdownMenuSubTriggerProps) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        'focus:bg-accentSecondary focus:text-accentSecondary-foreground data-[state=open]:bg-accentSecondary data-[state=open]:text-accentSecondary-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8',
        className
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-4" />
    </DropdownMenuPrimitive.SubTrigger>
  )
}

function DropdownMenuSubContent({
  className,
  size = 'content',
  padding = 'default',
  surface = 'default',
  tone = 'default',
  overflow = 'hidden',
  ...props
}: DropdownMenuSubContentProps) {
  return (
    <DropdownMenuPrimitive.SubContent
      data-slot="dropdown-menu-sub-content"
      data-overflow={overflow}
      data-padding={padding}
      data-size={size}
      data-surface={surface}
      data-tone={tone}
      className={cn(
        dropdownMenuContentVariants({ size, padding, surface, tone, overflow }),
        className
      )}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuArrow,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  dropdownMenuContentVariants,
  dropdownMenuItemVariants,
}
